import { useQuery } from "@tanstack/react-query"
import type { SxMarket } from "../types/markets"
import { fetchOrders } from "../api"
import useChainProviderHook from "@/hooks/useChainProvider"

/**
 * Create a simple hash from an array of strings
 * Used to create shorter, stable query keys from market hashes
 */
const createHashFromArray = (arr: string[]): string => {
  const sorted = arr.sort().join(",")
  let hash = 0
  for (let i = 0; i < sorted.length; i++) {
    const char = sorted.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash // Convert to 32bit integer
  }
  return hash.toString(36) // Convert to base36 for shorter string
}

/**
 * Reusable hook for fetching market odds with proper error handling
 * Prevents 500 errors from restarting the entire query chain
 */
export const useMarketOddsQuery = (
  markets: SxMarket[],
  contextLabel?: string,
  options?: {
    enabled?: boolean
    batchSize?: number
    staleTime?: number
    gcTime?: number
    retry?: number
    retryDelay?: (attemptIndex: number) => number
  }
) => {
  const { chainId } = useChainProviderHook()
  const {
    batchSize = 100,
    staleTime = 1000 * 60 * 10, // 10 minutes
    gcTime = 1000 * 60 * 10, // 10 minutes
    retry = 2,
    retryDelay = (attemptIndex: number) =>
      Math.min(1000 * 2 ** attemptIndex, 30000),
  } = options || {}

  const marketHashes = markets.map(m => m.marketHash).filter(Boolean)
  const marketsHash =
    marketHashes.length > 0 ? createHashFromArray(marketHashes) : "empty"

  return useQuery({
    queryKey: [
      "market-odds",
      marketsHash,
      marketHashes.length, // Include count for additional uniqueness
      contextLabel,
      chainId,
    ],
    queryFn: async () => {
      if (!markets || markets.length === 0) {
        return []
      }

      let allOrders: any[] = []

      // Process markets in batches to avoid overwhelming the API
      for (let i = 0; i < markets.length; i += batchSize) {
        const batch = markets.slice(i, i + batchSize)
        const batchMarketHashes = batch
          .map((market: SxMarket) => market.marketHash)
          .filter(Boolean)

        try {
          const { data } = await fetchOrders(batchMarketHashes, chainId)
          allOrders = [...allOrders, ...data]
        } catch (error) {
          console.error(
            `Error fetching odds for batch ${Math.floor(i / batchSize) + 1}:`,
            error
          )

          // For 500 errors or other API errors, continue with other batches
          // instead of failing the entire query
          if (error instanceof Error && error.message.includes("500")) {
            console.warn(
              `Skipping batch ${Math.floor(i / batchSize) + 1} due to 500 error`
            )
            continue
          }

          // For other errors, still continue but log them
          console.warn(
            `Continuing despite error in batch ${Math.floor(i / batchSize) + 1}:`,
            error
          )
        }
      }

      return allOrders
    },
    enabled: markets.length > 0,
    staleTime,
    gcTime,
    retry,
    retryDelay,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: true,
  })
}

/**
 * Helper function to filter markets that have corresponding odds
 */
export const filterMarketsWithOdds = (
  markets: SxMarket[],
  orders: any[]
): SxMarket[] => {
  return markets.filter(market =>
    orders.some(order => order.marketHash === market.marketHash)
  )
}
